import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, InputNumber, Typography, Space, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { collection, getDocs, addDoc, query, where, serverTimestamp, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";

const { Title } = Typography;

const Factures = () => {
  const [medecins, setMedecins] = useState([]);
  const [factureModalVisible, setFactureModalVisible] = useState(false);
  const [paiementModalVisible, setPaiementModalVisible] = useState(false);
  const [currentMedecin, setCurrentMedecin] = useState(null);
  const [factureForm] = Form.useForm();
  const [paiementForm] = Form.useForm();

  // New state for patients and patient modals
  const [patients, setPatients] = useState([]);
  const [patientsModalVisible, setPatientsModalVisible] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientFactureModalVisible, setPatientFactureModalVisible] = useState(false);
  const [patientPaiementModalVisible, setPatientPaiementModalVisible] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentForm] = Form.useForm();
  const [mode, setMode] = useState("facture"); // 'facture' or 'paiement'

  // Fetch medecins data
  useEffect(() => {
    const fetchMedecins = async () => {
      try {
        const medecinsSnapshot = await getDocs(collection(db, "medecins"));
        const medecinsList = medecinsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setMedecins(medecinsList);
      } catch (error) {
        /*console.error("Error fetching medecins:", error);*/
      }
    };
    fetchMedecins();
  }, []);

  // Fetch patients for a medecin
  const fetchPatientsForMedecin = async (medecin, filterMode = "facture") => {
    setLoadingPatients(true);
    try {
      const patientsQuery = query(
        collection(db, "patients"),
        where("userId", "==", medecin.uid)
      );
      const patientsSnapshot = await getDocs(patientsQuery);
      let patientsList = patientsSnapshot.docs.map((doc) => ({
        key: doc.id,
        ...doc.data(),
      }));
      if (filterMode === "paiement") {
        patientsList = patientsList.filter(p => p.factureDate);
      } else {
        patientsList = patientsList.filter(p => p.price !== undefined && p.price !== null);
      }
      setPatients(patientsList);
    } catch (error) {
      setPatients([]);
    }
    setLoadingPatients(false);
  };

  const showFactureModal = async (medecin) => {
    setCurrentMedecin(medecin);
    setMode("facture");
    factureForm.resetFields();
    await fetchPatientsForMedecin(medecin, "facture");
    setPatientsModalVisible(true);
  };

  const showPaiementModal = async (medecin) => {
    setCurrentMedecin(medecin);
    setMode("paiement");
    paiementForm.resetFields();
    await fetchPatientsForMedecin(medecin, "paiement");
    setPatientsModalVisible(true);
  };

  // Patient table columns
  const patientColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Surname", dataIndex: "surname", key: "surname" },
  ];

  // When a patient is clicked in the table
  const handlePatientRowClick = (patient, isPayment = false) => {
    setSelectedPatient(patient);
    if (isPayment) {
      setPatientPaiementModalVisible(true);
      paymentForm.resetFields();
    } else {
      setPatientFactureModalVisible(true);
    }
  };

  // Calculate values for the facture detail modal
  const getFactureDetails = () => {
    if (!selectedPatient || !currentMedecin) return null;
    const pack = selectedPatient.pack || "-";
    const basePrice = selectedPatient.basePrice || selectedPatient.price || 0;
    const remise = selectedPatient.discount || 0;
    const finalPrice = basePrice - (basePrice * remise / 100);
    const tva = finalPrice * 0.19;
    const afterTax = finalPrice + tva;
    return {
      medecinName: currentMedecin.name,
      patientName: `${selectedPatient.name} ${selectedPatient.surname || ''}`,
      pack,
      basePrice,
      finalPrice,
      remise,
      taxPercent: 19,
      tva,
      afterTax,
    };
  };

  // Calculate payment details
  const getPaymentDetails = () => {
    if (!selectedPatient || !currentMedecin) return null;
    const basePrice = typeof selectedPatient.basePrice === 'number' ? selectedPatient.basePrice : (typeof selectedPatient.price === 'number' ? selectedPatient.price : 0);
    const discount = typeof selectedPatient.discount === 'number' ? selectedPatient.discount : 0;
    const discountAmount = (basePrice * discount) / 100;
    const sousTotal = basePrice - discountAmount;
    const tva = sousTotal * 0.19;
    const timbre = 1.0;
    const totalPrice = sousTotal + tva + timbre;
    const paidAmount = typeof selectedPatient.payedAmount === 'number' ? selectedPatient.payedAmount : 0;
    const remainingAmount = totalPrice - paidAmount;
    return {
      medecinName: currentMedecin.name,
      patientName: `${selectedPatient.name} ${selectedPatient.surname || ''}`,
      totalPrice,
      paidAmount,
      remainingAmount,
      basePrice,
      discount,
      discountAmount,
      sousTotal,
      tva,
      timbre
    };
  };

  // Confirm facture for patient
  const handleConfirmFacture = async () => {
    if (!selectedPatient) return;
    setConfirmLoading(true);
    try {
      const patientRef = doc(db, "patients", selectedPatient.key);
      const details = getFactureDetails();
      
      await updateDoc(patientRef, { 
        factureDate: new Date().toISOString(),
        totalPrice: details.afterTax // Save total price to avoid recalculation
      });
      
      setPatientFactureModalVisible(false);
      setPatientsModalVisible(false);
      setSelectedPatient(null);
      message.success('Facture confirmée avec succès');
    } catch (error) {
      message.error('Erreur lors de la confirmation de la facture');
    }
    setConfirmLoading(false);
  };

  // Handle payment submission
  const handlePaymentSubmit = async (values) => {
    if (!selectedPatient) return;
    setPaymentLoading(true);
    
    try {
      const patientRef = doc(db, "patients", selectedPatient.key);
      const paymentDetails = getPaymentDetails();
      const paymentAmount = values.montant;
      
      if (paymentAmount > paymentDetails.remainingAmount) {
        message.error('Le montant ne peut pas dépasser le montant restant');
        setPaymentLoading(false);
        return;
      }
      
      const newPaidAmount = (selectedPatient.payedAmount || 0) + paymentAmount;
      const newRemainingAmount = paymentDetails.totalPrice - newPaidAmount;
      
      await updateDoc(patientRef, {
        paiementDate: new Date().toISOString(),
        payedAmount: newPaidAmount,
        remainingAmount: newRemainingAmount
      });
      
      // Add payment record to factures collection
      await addDoc(collection(db, "factures"), {
        medecinId: currentMedecin.key,
        medecinName: currentMedecin.name,
        patientId: selectedPatient.key,
        patientName: paymentDetails.patientName,
        montant: paymentAmount,
        date: serverTimestamp(),
        type: "paiement"
      });
      
      setPatientPaiementModalVisible(false);
      setPatientsModalVisible(false);
      setSelectedPatient(null);
      paymentForm.resetFields();
      message.success('Paiement enregistré avec succès');
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement du paiement');
    }
    setPaymentLoading(false);
  };

  const handleFactureSubmit = async (values) => {
    try {
      await addDoc(collection(db, "factures"), {
        medecinId: currentMedecin.key,
        medecinName: currentMedecin.name,
        montant: values.montant,
        date: serverTimestamp(),
        type: "facture"
      });
      
      setFactureModalVisible(false);
      factureForm.resetFields();
    } catch (error) {
      /*console.error("Error adding facture:", error);*/
    }
  };

  const handlePaiementSubmit = async (values) => {
    try {
      await addDoc(collection(db, "factures"), {
        medecinId: currentMedecin.key,
        medecinName: currentMedecin.name,
        montant: values.montant,
        date: serverTimestamp(),
        type: "paiement"
      });
      
      setPaiementModalVisible(false);
      paiementForm.resetFields();
    } catch (error) {
      /*console.error("Error adding paiement:", error);*/
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Medecin Type",
      dataIndex: "role",
      key: "role",
      render: (role) => role || "N/A",
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phoneNumber) => phoneNumber || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            onClick={() => showFactureModal(record)}
          >
            Ajouter Facture
          </Button>
          <Button 
            onClick={() => showPaiementModal(record)}
          >
            Ajouter Paiement
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>Factures et Paiements</Title>
      <Table dataSource={medecins} columns={columns} rowKey="key" />
      
      {/* Patients Modal */}
      <Modal
        title={`Liste des patients de ${currentMedecin ? currentMedecin.name : ''}`}
        visible={patientsModalVisible}
        onCancel={() => setPatientsModalVisible(false)}
        footer={null}
        width={'100vw'}
      >
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Table
            dataSource={patients}
            columns={patientColumns}
            rowKey="key"
            loading={loadingPatients}
            onRow={(record) => ({
              onClick: () => handlePatientRowClick(record, mode === "paiement"),
              style: { cursor: 'pointer' },
            })}
            pagination={false}
            style={{ width: '100%', minWidth: '1100px' }}
          />
        </div>
      </Modal>

      {/* Patient Payment Details Modal */}
      <Modal
        title="Détails du Paiement"
        visible={patientPaiementModalVisible}
        onCancel={() => setPatientPaiementModalVisible(false)}
        footer={null}
      >
        {selectedPatient && currentMedecin && (
          (() => {
            const details = getPaymentDetails();
            return (
              <div>
                <p><b>Patient:</b> {details.patientName}</p>
                <p>
                  <b>Somme à payer:</b> 
                  <span title={`Prix: ${details.basePrice.toFixed(2)} - Remise: ${details.discountAmount.toFixed(2)} + TVA: ${details.tva.toFixed(2)} + Timbre: ${details.timbre.toFixed(2)}`}>
                    {details.totalPrice.toFixed(2)} TND
                  </span>
                </p>
                <p><b>Somme payée:</b> {details.paidAmount.toFixed(2)} TND</p>
                <p><b>Somme restante:</b> {details.remainingAmount.toFixed(2)} TND</p>
                <Form
                  form={paymentForm}
                  layout="vertical"
                  onFinish={handlePaymentSubmit}
                >
                  <Form.Item
                    label="Montant à payer (TND)"
                    name="montant"
                    rules={[
                      { required: true, message: "Veuillez saisir le montant!" },
                      {
                        validator: (_, value) => {
                          if (value && value > details.remainingAmount) {
                            return Promise.reject(new Error(`Le montant ne peut pas dépasser ${details.remainingAmount.toFixed(2)} TND`));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <InputNumber 
                      min={0} 
                      max={details.remainingAmount}
                      style={{ width: '100%' }} 
                      formatter={value => `TND ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/TND\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" block loading={paymentLoading}>
                      Confirmer
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            );
          })()
        )}
      </Modal>

      {/* Patient Facture Details Modal */}
      <Modal
        title="Détails de la Facture"
        visible={patientFactureModalVisible}
        onCancel={() => setPatientFactureModalVisible(false)}
        footer={null}
      >
        {selectedPatient && currentMedecin && (
          (() => {
            const details = getFactureDetails();
            return (
              <div>
                <p><b>Médecin:</b> {details.medecinName}</p>
                <p><b>Patient:</b> {details.patientName}</p>
                <p><b>Pack:</b> {details.pack}</p>
                <p><b>Base Price:</b> {details.basePrice} TND</p>
                <p><b>Final Price:</b> {details.finalPrice} TND</p>
                <p><b>Remise:</b> {details.remise ? `${details.remise}%` : '0%'}</p>
                <p><b>Taxe (19%):</b> {details.tva} TND</p>
                <p><b>Total après taxe:</b> {details.afterTax} TND</p>
                <Button type="primary" block loading={confirmLoading} onClick={handleConfirmFacture} style={{ marginTop: 16 }}>
                  Confirmer
                </Button>
              </div>
            );
          })()
        )}
      </Modal>
      
      {/* Facture Modal */}
      <Modal
        title="Ajouter une Facture"
        visible={factureModalVisible}
        onCancel={() => setFactureModalVisible(false)}
        footer={null}
      >
        <Form
          form={factureForm}
          layout="vertical"
          onFinish={handleFactureSubmit}
        >
          <Form.Item
            label="Montant"
            name="montant"
            rules={[{ required: true, message: "Veuillez saisir le montant!" }]}
          >
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              formatter={value => `TND ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/TND\s?|(,*)/g, '')}
            />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Enregistrer
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Paiement Modal */}
      <Modal
        title="Ajouter un Paiement"
        visible={paiementModalVisible}
        onCancel={() => setPaiementModalVisible(false)}
        footer={null}
      >
        <Form
          form={paiementForm}
          layout="vertical"
          onFinish={handlePaiementSubmit}
        >
          <Form.Item
            label="Montant"
            name="montant"
            rules={[{ required: true, message: "Veuillez saisir le montant!" }]}
          >
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              formatter={value => `TND ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/TND\s?|(,*)/g, '')}
            />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Enregistrer
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Factures; 